import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getMenuItems } from '../lib/cms';

export default function Layout({ children }) {
  const [menuItems, setMenuItems] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    async function loadMenu() {
      const items = await getMenuItems();
      setMenuItems(items);
    }
    loadMenu();
  }, []);

  // Luk mobilmenuen ved ruteskift
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (href) => {
    if (href === '/' && location.pathname === '/') return true;
    if (href !== '/' && location.pathname.startsWith(href)) return true;
    return false;
  };

  return (
    <div className="flex min-h-screen flex-col bg-transparent text-chocolate font-sans">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-chocolate/95 backdrop-blur-md text-cream shadow-md border-b border-gold-brown/20">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="https://assets.auxodev.dk/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/logo-negativ.svg"
              alt="Vissenbjerg Bageri"
              className="h-12 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation (Viser kun ved >= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center gap-1">
            {menuItems.map((item) => (
              <Link
                key={item.id || item.href}
                to={item.href}
                className={`px-4 py-2 rounded-md text-sm font-medium tracking-wide transition-all duration-200 ${
                  isActive(item.href)
                    ? 'text-gold-brown bg-cream/5 font-semibold'
                    : 'text-cream/80 hover:text-gold-brown hover:bg-cream/5'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Mobile Menu Button (Viser kun ved < 1200px) */}
          <div className="flex min-[1200px]:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              type="button"
              className="inline-flex items-center justify-center rounded-md p-2 text-cream/80 hover:bg-cream/5 hover:text-gold-brown focus:outline-none"
              aria-expanded="false"
            >
              <span className="sr-only">Åbn menu</span>
              <i className={`fa-solid ${isMobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`} />
            </button>
          </div>
        </div>

        {/* Mobile Navigation Dropdown (Viser kun ved < 1200px) */}
        {isMobileMenuOpen && (
          <nav className="min-[1200px]:hidden bg-chocolate border-t border-gold-brown/10 px-4 py-3 space-y-1 shadow-inner animate-fadeIn">
            {menuItems.map((item) => (
              <Link
                key={item.id || item.href}
                to={item.href}
                className={`block px-4 py-3 rounded-md text-base font-medium transition-colors ${
                  isActive(item.href)
                    ? 'text-gold-brown bg-cream/5 font-semibold'
                    : 'text-cream/80 hover:text-gold-brown hover:bg-cream/5'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="relative bg-chocolate text-cream border-t-2 border-gold-brown/30">
        {/* Bageri dekoration */}
        <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-chocolate px-6 py-1.5 rounded-full border border-gold-brown/30 shadow-lg flex items-center justify-center">
          <img
            src="https://assets.auxodev.dk/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/decor-bakery_1.png"
            alt="Bageri dekoration"
            className="h-10 w-auto brightness-0 invert opacity-90"
          />
        </div>

        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12">
            {/* Om bageriet */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img
                  src="https://assets.auxodev.dk/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/logo-negativ.svg"
                  alt="Vissenbjerg Bageri"
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-sm text-cream/70 leading-relaxed max-w-sm">
                Siden 1925 har vi bagt med kærlighed og respekt for det gamle bagerhåndværk i hjertet af Vissenbjerg. Kåret til Fyns bedste bageri.
              </p>
              <div className="flex items-center gap-4 pt-2">
                <a
                  href="https://www.facebook.com/vissenbjergbageri.dk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-10 w-10 rounded-full bg-cream/5 flex items-center justify-center text-cream/80 hover:text-gold-brown hover:bg-cream/10 transition-all duration-200"
                  aria-label="Følg os på Facebook"
                >
                  <i className="fa-brands fa-facebook-f text-lg" />
                </a>
                <a
                  href="https://www.instagram.com/vissenbjergbageri"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-10 w-10 rounded-full bg-cream/5 flex items-center justify-center text-cream/80 hover:text-gold-brown hover:bg-cream/10 transition-all duration-200"
                  aria-label="Følg os på Instagram"
                >
                  <i className="fa-brands fa-instagram text-lg" />
                </a>
              </div>
              <div className="pt-2">
                <a
                  href="https://brunsvigermand.dk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-gold-brown hover:text-cream transition-colors"
                >
                  <i className="fa-solid fa-cake-candles" />
                  <span>Bestil kagemand på brunsvigermand.dk</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" />
                </a>
              </div>
            </div>

            {/* Åbningstider */}
            <div className="space-y-4">
              <h3 className="font-serif text-lg font-bold text-gold-brown tracking-wide">Åbningstider</h3>
              <ul className="space-y-2 text-sm text-cream/80">
                <li className="flex justify-between border-b border-cream/10 pb-2">
                  <span>Mandag – Fredag</span>
                  <span className="font-semibold">kl. 06.00-17.00</span>
                </li>
                <li className="flex justify-between border-b border-cream/10 pb-2">
                  <span>Lørdag – Søndag</span>
                  <span className="font-semibold">kl. 06.00-15.30</span>
                </li>
              </ul>
              <div className="flex items-center gap-2 text-xs text-gold-brown/80 pt-1">
                <i className="fa-solid fa-circle-info" />
                <span>Friskbagt brød og kager hele dagen</span>
              </div>
            </div>

            {/* Kontakt & Info */}
            <div className="space-y-4">
              <h3 className="font-serif text-lg font-bold text-gold-brown tracking-wide">Kontakt os</h3>
              <ul className="space-y-3 text-sm text-cream/80">
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-location-dot text-gold-brown mt-1" />
                  <a
                    href="https://goo.gl/maps/wFjyWbJ8J69dYnSA9"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-gold-brown transition-colors"
                  >
                    Vestergade 5, 5492 Vissenbjerg
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-phone text-gold-brown" />
                  <a
                    href="tel:64471266"
                    className="hover:text-gold-brown transition-colors whitespace-nowrap"
                  >
                    64 47 12 66
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-envelope text-gold-brown" />
                  <a
                    href="mailto:info@vissenbjergbageri.dk"
                    className="hover:text-gold-brown transition-colors whitespace-nowrap"
                  >
                    info@vissenbjergbageri.dk
                  </a>
                </li>
                <li className="flex items-center gap-3 border-t border-cream/10 pt-3">
                  <span className="text-xs text-cream/50">CVR nr.:</span>
                  <span className="text-xs font-semibold tracking-wider">30134400</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Bund */}
          <div className="mt-12 border-t border-cream/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-cream/40">
            <p>© {new Date().getFullYear()} Vissenbjerg Bageri. Alle rettigheder forbeholdes.</p>
            <p className="flex items-center gap-2">
              <span>Håndlavet med kærlighed på Fyn</span>
              <i className="fa-solid fa-heart text-copper" />
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
